import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService from '../services/apiService';
import { ExportConfiguration, ExportPushResult, MFilesSearchResult, SummaryRef } from '../types';
import { logger } from '../utils/logger';

interface MFilesExportFlowProps {
    fileId: string;
    summaryRef: SummaryRef;
    /** Alle M-Files-configuraties voor dit template (bv. meerdere vaults) */
    configs: ExportConfiguration[];
    summaryTitle: string;
    /** Terug naar het gewone deelmenu */
    onBack: () => void;
    /** Hele deel-dialoog sluiten */
    onClose: () => void;
    /**
     * Aangeroepen na een 404: de configuratie bestaat niet meer of is
     * uitgezet. Het deelmenu verbergt de exportoptie en haalt de
     * configuraties opnieuw op.
     */
    onConfigurationsStale: () => void;
}

// DOCUMENT_LINKED: zoeken → object kiezen → bevestigen → link-call → resultaat.
// DOCUMENT: bevestigen → push-call → resultaat (geen zoekstap).
type FlowStep = 'start' | 'confirm' | 'sending' | 'success' | 'error';

// Elke zoekopdracht gaat live naar de vault van de klant: pas zoeken als de
// gebruiker even stil is, en niet op één losse letter.
const SEARCH_DEBOUNCE_MS = 500;
const MIN_SEARCH_LENGTH = 2;

// Spiegel van de backend-validatie: zoekterm maximaal 200 tekens.
const MAX_SEARCH_LENGTH = 200;

// 400 (ongeldige invoer), 403 (geen toegang) en 404 (configuratie weg) worden
// niet beter van opnieuw proberen; alleen 500 en netwerkfouten wel.
const isRetryableStatus = (status?: number): boolean =>
    status !== 400 && status !== 403 && status !== 404;

const MFilesExportFlow: React.FC<MFilesExportFlowProps> = ({
    fileId,
    summaryRef,
    configs,
    summaryTitle,
    onBack,
    onClose,
    onConfigurationsStale,
}) => {
    const { t, i18n } = useTranslation();
    const apiService = useMemo(() => new ApiService(), []);

    const [step, setStep] = useState<FlowStep>('start');
    const [selectedConfigId, setSelectedConfigId] = useState<string>(configs[0]?.id ?? '');
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [searchErrorRetryable, setSearchErrorRetryable] = useState(true);
    // Klantnamen uit de vault: uitsluitend in deze component-state houden;
    // niet persisten, niet loggen, niet doorgeven aan analytics.
    const [results, setResults] = useState<MFilesSearchResult[]>([]);
    const [moreResults, setMoreResults] = useState(false);
    const [linkConfigId, setLinkConfigId] = useState<string>('');
    const [selectedResult, setSelectedResult] = useState<MFilesSearchResult | null>(null);
    const [sendResult, setSendResult] = useState<ExportPushResult | null>(null);
    const [sendErrorRetryable, setSendErrorRetryable] = useState(true);
    const [configsStale, setConfigsStale] = useState(false);

    const selectedConfig = configs.find((config) => config.id === selectedConfigId) ?? configs[0];
    // Zonder expliciete export_method uitgaan van de zoekflow: een push op een
    // configuratie die koppelen vereist wordt door de backend geweigerd.
    const isDirectFlow = selectedConfig?.exportMethod === 'DOCUMENT';

    const trimmedTerm = searchTerm.trim();
    const termTooShort = trimmedTerm.length < MIN_SEARCH_LENGTH;
    // config_id alleen meesturen als er echt iets te kiezen valt; bij één
    // configuratie lost de backend hem zelf op.
    const searchConfigId = configs.length > 1 ? selectedConfigId : undefined;

    // Laat antwoorden van eerdere toetsaanslagen een nieuwer resultaat niet
    // overschrijven.
    const requestSeq = useRef(0);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Laatst automatisch gezochte zoekterm + vault. Voorkomt dat een
    // her-render (bv. taalwissel) dezelfde vraag nog eens naar de vault stuurt.
    const lastAutoSearch = useRef<string | null>(null);

    const runSearch = useCallback(
        async (term: string, configId?: string) => {
            const seq = requestSeq.current + 1;
            requestSeq.current = seq;
            setIsSearching(true);
            setSearchError(null);
            try {
                const response = await apiService.searchMFiles(fileId, summaryRef, term, configId);
                if (requestSeq.current !== seq) return;
                setResults(response.results);
                setMoreResults(response.moreResults);
                setLinkConfigId(response.configId);
                setHasSearched(true);
            } catch (error: any) {
                if (requestSeq.current !== seq) return;
                // Alleen de foutstatus loggen; response-data kan klantnamen bevatten.
                const status = error?.response?.status;
                logger.error('M-Files search failed with status:', status);
                if (status === 404) setConfigsStale(true);
                const message = error?.response?.data?.error;
                setSearchError(message ? message.toString() : t('shareDialog.mfiles.error'));
                setSearchErrorRetryable(isRetryableStatus(status));
                setResults([]);
                setMoreResults(false);
                setHasSearched(false);
            } finally {
                if (requestSeq.current === seq) {
                    setIsSearching(false);
                }
            }
        },
        [apiService, fileId, summaryRef, t]
    );

    // Debounce: pas zoeken als de gebruiker even niet typt.
    useEffect(() => {
        if (isDirectFlow) return;
        if (trimmedTerm.length < MIN_SEARCH_LENGTH) {
            // Een lopende zoekactie mag de lege lijst niet meer vullen.
            requestSeq.current += 1;
            lastAutoSearch.current = null;
            setIsSearching(false);
            setResults([]);
            setMoreResults(false);
            setHasSearched(false);
            setSearchError(null);
            return;
        }
        const key = `${searchConfigId ?? ''}|${trimmedTerm}`;
        if (lastAutoSearch.current === key) return;
        const timer = setTimeout(() => {
            lastAutoSearch.current = key;
            runSearch(trimmedTerm, searchConfigId);
        }, SEARCH_DEBOUNCE_MS);
        debounceTimer.current = timer;
        return () => clearTimeout(timer);
    }, [trimmedTerm, searchConfigId, isDirectFlow, runSearch]);

    // Enter laat de gebruiker de debounce overslaan.
    const searchNow = () => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
            debounceTimer.current = null;
        }
        if (!termTooShort) {
            lastAutoSearch.current = `${searchConfigId ?? ''}|${trimmedTerm}`;
            runSearch(trimmedTerm, searchConfigId);
        }
    };

    // Resultaten horen bij één vault; bij wisselen opnieuw zoeken.
    const changeConfig = (configId: string) => {
        setSelectedConfigId(configId);
        lastAutoSearch.current = null;
        setResults([]);
        setMoreResults(false);
        setHasSearched(false);
        setSearchError(null);
    };

    const link = async () => {
        if (!selectedResult) return;
        setStep('sending');
        try {
            const result = await apiService.linkMFiles(
                fileId,
                summaryRef,
                linkConfigId || selectedConfigId,
                // Het object-id letterlijk doorgeven: niet samenstellen, niet parsen.
                selectedResult.id
            );
            setSendResult(result);
            setSendErrorRetryable(true);
            setStep(result.success ? 'success' : 'error');
        } catch (error: any) {
            const status = error?.response?.status;
            logger.error('M-Files link failed with status:', status);
            if (status === 404) setConfigsStale(true);
            const message = error?.response?.data?.error;
            setSendResult({
                success: false,
                message: message ? message.toString() : t('shareDialog.mfiles.error'),
            });
            setSendErrorRetryable(isRetryableStatus(status));
            setStep('error');
        }
    };

    const push = async () => {
        setStep('sending');
        try {
            const result = await apiService.pushToMFiles(fileId, summaryRef, selectedConfigId);
            setSendResult(result);
            setSendErrorRetryable(true);
            setStep(result.success ? 'success' : 'error');
        } catch (error: any) {
            const status = error?.response?.status;
            logger.error('M-Files push failed with status:', status);
            if (status === 404) setConfigsStale(true);
            const message = error?.response?.data?.error;
            setSendResult({
                success: false,
                message: message ? message.toString() : t('shareDialog.mfiles.error'),
            });
            setSendErrorRetryable(isRetryableStatus(status));
            setStep('error');
        }
    };

    // Na een 404 is de exportoptie zelf niet meer geldig: terug betekent dan
    // opnieuw configuraties ophalen in plaats van terug naar deze flow.
    const goBack = () => {
        if (configsStale) {
            onConfigurationsStale();
            return;
        }
        onBack();
    };

    const formatLastModified = (value?: string): string => {
        if (!value) return '';
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(i18n.language);
    };

    // "Nr. 123 · 18-08-2026"
    const formatMeta = (result: MFilesSearchResult): string =>
        [
            result.displayId ? t('shareDialog.mfiles.displayId', { id: result.displayId }) : '',
            formatLastModified(result.lastModified),
        ]
            .filter(Boolean)
            .join(' · ');

    const inputClass =
        'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500';
    const primaryButtonClass =
        'px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
    const secondaryButtonClass =
        'px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors';

    const configPicker = configs.length > 1 && (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('shareDialog.mfiles.configLabel')}
            </label>
            <select
                value={selectedConfigId}
                onChange={(e) => changeConfig(e.target.value)}
                disabled={isSearching}
                className={inputClass}
            >
                {configs.map((config) => (
                    <option key={config.id} value={config.id}>{config.name}</option>
                ))}
            </select>
        </div>
    );

    // DOCUMENT: geen zoekstap, alleen een bevestiging vooraf.
    if (step === 'start' && isDirectFlow) {
        return (
            <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('shareDialog.mfiles.directTitle')}
                </h3>
                {configPicker}
                <p className="text-sm text-gray-900">
                    {t('shareDialog.mfiles.directConfirm', { title: summaryTitle })}
                </p>
                <div className="flex justify-end space-x-2">
                    <button onClick={goBack} className={secondaryButtonClass}>
                        {t('shareDialog.mfiles.back')}
                    </button>
                    <button onClick={push} className={primaryButtonClass}>
                        {t('shareDialog.mfiles.directAction')}
                    </button>
                </div>
            </div>
        );
    }

    if (step === 'start') {
        return (
            <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('shareDialog.mfiles.searchTitle')}
                </h3>

                {configPicker}

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('shareDialog.mfiles.searchLabel')}
                    </label>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                searchNow();
                            }
                        }}
                        maxLength={MAX_SEARCH_LENGTH}
                        autoFocus
                        className={inputClass}
                    />
                </div>

                {trimmedTerm.length > 0 && termTooShort && (
                    <p className="text-xs text-gray-500">{t('shareDialog.mfiles.searchMinChars')}</p>
                )}

                <div className="flex justify-end">
                    <button onClick={goBack} className={secondaryButtonClass}>
                        {t('shareDialog.mfiles.back')}
                    </button>
                </div>

                {isSearching && (
                    <div className="flex justify-center py-4">
                        <div className="w-5 h-5 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin"></div>
                    </div>
                )}

                {searchError && !isSearching && (
                    <div className="text-center space-y-2">
                        <p className="text-sm text-red-600">{searchError}</p>
                        {searchErrorRetryable && (
                            <button onClick={searchNow} className={secondaryButtonClass}>
                                {t('shareDialog.mfiles.retry')}
                            </button>
                        )}
                    </div>
                )}

                {hasSearched && !isSearching && !searchError && results.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-2">
                        {t('shareDialog.mfiles.empty')}
                    </p>
                )}

                {hasSearched && !isSearching && !searchError && results.length > 0 && (
                    <div>
                        <div className="text-sm font-medium text-gray-700 mb-2">
                            {t('shareDialog.mfiles.resultsLabel')}
                        </div>
                        {moreResults && (
                            <div className="mb-2 px-3 py-2 text-xs rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                                {t('shareDialog.mfiles.moreResults')}
                            </div>
                        )}
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {results.map((result) => {
                                const meta = formatMeta(result);
                                return (
                                    <button
                                        key={result.id}
                                        onClick={() => {
                                            setSelectedResult(result);
                                            setStep('confirm');
                                        }}
                                        className="w-full flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-gray-900 text-sm truncate">{result.name}</div>
                                            {meta && <div className="text-gray-500 text-xs truncate">{meta}</div>}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (step === 'confirm' && selectedResult) {
        const meta = formatMeta(selectedResult);
        return (
            <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('shareDialog.mfiles.searchTitle')}
                </h3>
                {/* Expliciete bevestiging: elke export maakt een nieuw document
                    in de vault, er wordt niets overschreven. */}
                <p className="text-sm text-gray-900">
                    {t('shareDialog.mfiles.confirm', {
                        title: summaryTitle,
                        name: selectedResult.name,
                    })}
                </p>
                {meta && <div className="text-xs text-gray-500">{meta}</div>}
                <div className="flex justify-end space-x-2">
                    <button onClick={() => setStep('start')} className={secondaryButtonClass}>
                        {t('shareDialog.mfiles.back')}
                    </button>
                    <button onClick={link} className={primaryButtonClass}>
                        {t('shareDialog.mfiles.confirmAction')}
                    </button>
                </div>
            </div>
        );
    }

    if (step === 'sending') {
        return (
            <div className="flex flex-col items-center py-8 text-center">
                <div className="w-8 h-8 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin mb-4"></div>
                <div className="text-sm font-medium text-gray-900">{t('shareDialog.mfiles.busy')}</div>
                <div className="text-xs text-gray-500 mt-1">{t('shareDialog.mfiles.busyHint')}</div>
            </div>
        );
    }

    if (step === 'success') {
        return (
            <div className="flex flex-col items-center py-6 text-center">
                <svg className="w-8 h-8 text-primary-600 mb-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-gray-900">
                    {sendResult?.message || t('shareDialog.mfiles.success')}
                </p>
                <div className="flex space-x-2 mt-4">
                    {sendResult?.url && (
                        <button
                            onClick={() => window.open(sendResult.url, '_blank', 'noopener,noreferrer')}
                            className={primaryButtonClass}
                        >
                            {t('shareDialog.mfiles.open')}
                        </button>
                    )}
                    <button onClick={onClose} className={secondaryButtonClass}>
                        {t('shareDialog.mfiles.close')}
                    </button>
                </div>
            </div>
        );
    }

    // step === 'error' (of confirm zonder selectie, wat niet voor hoort te komen)
    return (
        <div className="flex flex-col items-center py-6 text-center">
            <svg className="w-8 h-8 text-red-600 mb-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {/* Foutmeldingen van de backend zijn Nederlands en letterlijk
                bedoeld (bv. rechten van het serviceaccount): niet herschrijven. */}
            <p className="text-sm text-gray-900">
                {sendResult?.message || t('shareDialog.mfiles.error')}
            </p>
            <div className="flex space-x-2 mt-4">
                {/* Geen automatische retries: elke poging maakt een nieuw
                    document in de vault, dus opnieuw proberen is een bewuste actie. */}
                {sendErrorRetryable && (
                    <button onClick={isDirectFlow ? push : link} className={primaryButtonClass}>
                        {t('shareDialog.mfiles.retry')}
                    </button>
                )}
                <button
                    onClick={() => (configsStale ? goBack() : setStep('start'))}
                    className={secondaryButtonClass}
                >
                    {t('shareDialog.mfiles.back')}
                </button>
            </div>
        </div>
    );
};

export default MFilesExportFlow;
